import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Tabs,
  Tab,
  Paper,
  InputBase,
  Badge,
  Tooltip,
  Fade,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import HistoryIcon from "@mui/icons-material/History";
import StarIcon from "@mui/icons-material/Star";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "./firebase";
import { motion } from "framer-motion";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { SubjectsList, GradeLevels } from "./BankFilters";
import {
  lessonMapping,
  getLessonTypeParam,
  lessonUrlMapping,
} from "./functions";

// Animation variants for smooth transitions
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const LessonCard = ({ lesson, onClick, theme }) => {
  const formattedDate = lesson.timestamp
    ? new Date(lesson.timestamp).toLocaleDateString("sv-SE", {
        day: "numeric",
        month: "short",
      })
    : "";

  return (
    <motion.div variants={itemVariants}>
      <Card
        onClick={() => onClick(lesson)}
        sx={{
          display: "flex",
          cursor: "pointer",
          borderRadius: 3,
          overflow: "hidden",
          height: "150px",
          position: "relative",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.25)"
              : "0 4px 20px rgba(0,0,0,0.05)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 12px 28px rgba(0,0,0,0.4)"
                : "0 12px 28px rgba(0,0,0,0.15)",
          },
          "&:after":
            lesson.upvotes > 15
              ? {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "47px",
                  height: "47px",
                  background: `linear-gradient(45deg, transparent 50%, ${theme.palette.warning.light} 50%)`,
                  zIndex: 1,
                }
              : {},
        }}
      >
        {lesson.upvotes > 15 && (
          <Tooltip title="Populär lektion">
            <AutoAwesomeIcon
              sx={{
                position: "absolute",
                top: "6px",
                right: "6px",
                color: "white",
                zIndex: 2,
                fontSize: "1rem",
              }}
            />
          </Tooltip>
          //
        )}

        {lesson.upvotes > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              background: alpha(theme.palette.warning.main, 0.2),
              borderRadius: "12px",
              backdropFilter: "blur(4px)",
              px: 1,
              py: 0.5,
            }}
          >
            <ThumbUpOffAltIcon
              sx={{ fontSize: "0.8rem", color: theme.palette.warning.main }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: theme.palette.warning.main,
              }}
            >
              {lesson.upvotes}
            </Typography>
          </Box>
        )}
        <CardMedia
          component="img"
          sx={{
            width: 140,
            height: "100%",
            objectFit: "cover",
            borderRight: "1px solid",
            borderColor: "divider",
          }}
          image={
            lesson.coverImage ||
            `/img/lesson-types/${lessonMapping(lesson.lessonType).toLowerCase().replace(/\s+/g, "-")}.jpg`
          }
          alt={lesson.lessonName}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "calc(100% - 140px)",
          }}
        >
          <CardContent sx={{ flex: "1 0 auto", p: 2 }}>
            <Typography
              component="div"
              variant="h6"
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                mb: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {lesson.lessonName}
            </Typography>

            {lesson.comment && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: "0.85rem",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  mb: 1,
                }}
              >
                {lesson.comment}
              </Typography>
            )}

            <Box
              sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: "auto" }}
            >
              {lesson.tags?.slice(0, 3).map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    height: "20px",
                    "& .MuiChip-label": { px: 1 },
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.04)",
                  }}
                />
              ))}
              {lesson.tags?.length > 3 && (
                <Chip
                  label={`+${lesson.tags.length - 3}`}
                  size="small"
                  sx={{
                    fontSize: "0.7rem",
                    height: "20px",
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Box>
      </Card>
    </motion.div>
  );
};

const SwitchLessonsDrawer = ({
  open,
  onClose,
  user,
  styles,
  theme,
  lessonType,
}) => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [lessons, setLessons] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLessonTypes, setSelectedLessonTypes] = useState(
    lessonType ? [lessonMapping(lessonType)] : [],
  );

  const adminIds = [
    "Z1dF5PGfjvdBGS9egzACvcSRZiN2",
    "x2rjH7JxWxc6GnFgPSTxGxN1bm63",
    "Z6BLSMsm38Un5mgW0O6Z3TToHq12",
  ];

  useEffect(() => {
    const fetchLessons = async () => {
      setIsLoading(true);
      const lessonQuery = query(
        collection(db, "lesson_usage"),
        where("userId", "in", adminIds),
        where("featured", "==", true),
        where("tags", "!=", null),
      );

      try {
        const querySnapshot = await getDocs(lessonQuery);
        const fetchedLessons = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate(),
          }))
          .filter((lesson) => lesson.tags?.length > 0);

        setLessons(fetchedLessons);
      } catch (error) {
        console.error("Error fetching lessons:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchLessons();
    }
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleLessonClick = (lesson) => {
    const shortId = lesson.id.substring(0, 10);
    const typeParam = getLessonTypeParam(lesson.lessonType);
    const basePath =
      lesson.lessonType !== "PowerPointGenerator.js" ? "/lektioner" : "";
    const queryString = typeParam === "default" ? "" : `?type=${typeParam}`;
    navigate(
      `/app${basePath}/${lessonUrlMapping(lesson.lessonType)}/${shortId}${queryString}`,
    );
  };

  const clearFilters = () => {
    setSelectedSubjects([]);
    setSelectedGrades([]);
    setSelectedLessonTypes(lessonType ? [lessonMapping(lessonType)] : []);
    setSearchQuery("");
  };

  const filteredLessons = lessons
    .filter((lesson) => {
      let matchesSubject = true;
      let matchesGrade = true;
      let matchesLessonType = true;
      let matchesSearch = true;

      if (selectedSubjects.length > 0) {
        matchesSubject = selectedSubjects.some((subject) =>
          lesson.tags?.includes(subject),
        );
      }

      if (selectedGrades.length > 0) {
        matchesGrade = selectedGrades.some((grade) =>
          lesson.tags?.includes(grade),
        );
      }

      if (selectedLessonTypes.length > 0) {
        matchesLessonType = selectedLessonTypes.includes(
          lessonMapping(lesson.lessonType),
        );
      } else if (lessonType) {
        matchesLessonType =
          lessonMapping(lesson.lessonType) === lessonMapping(lessonType);
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        matchesSearch =
          lesson.lessonName?.toLowerCase().includes(query) ||
          lesson.comment?.toLowerCase().includes(query) ||
          lesson.tags?.some((tag) => tag.toLowerCase().includes(query));
      }

      return (
        matchesSubject && matchesGrade && matchesLessonType && matchesSearch
      );
    })
    .sort((a, b) => {
      // Sort by tab value
      if (tabValue === 0) {
        // Sort by upvotes for "Populära"
        return (b.upvotes || 0) - (a.upvotes || 0);
      } else if (tabValue === 1) {
        // Sort by date for "Nya"
        return new Date(b.timestamp || 0) - new Date(a.timestamp || 0);
      } else {
        // Sort alphabetically for "Alla"
        return a.lessonName?.localeCompare(b.lessonName);
      }
    });

  // Group lessons by their mapped type
  const groupedLessons = filteredLessons.reduce((acc, lesson) => {
    const mappedType = lessonMapping(lesson.lessonType);
    if (!acc[mappedType]) {
      acc[mappedType] = [];
    }
    acc[mappedType].push(lesson);
    return acc;
  }, {});

  const sortedLessonTypes = Object.keys(groupedLessons).sort((a, b) => {
    if (a === "Läsförståelse") return -1;
    if (b === "Läsförståelse") return 1;
    return a.localeCompare(b);
  });

  // Active filter count badge
  const activeFilterCount =
    selectedSubjects.length +
    selectedGrades.length +
    (selectedLessonTypes.length
      ? lessonType
        ? selectedLessonTypes.length - 1
        : selectedLessonTypes.length
      : 0) +
    (searchQuery ? 1 : 0);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.3)", // Adjust opacity here (0.3 = 30%)
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: { xs: "100%", sm: "480px" },
          backgroundColor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.background.default, 0.98)
              : theme.palette.background.default,
          backgroundImage:
            theme.palette.mode === "dark"
              ? "linear-gradient(to bottom, rgba(20, 20, 30, 0.95), rgba(10, 10, 20, 0.97))"
              : "linear-gradient(to bottom, rgba(250, 250, 255, 0.97), rgba(245, 247, 250, 0.99))",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 0 25px rgba(0, 0, 0, 0.5)"
              : "0 0 25px rgba(0, 0, 0, 0.15)",
          borderRadius: { xs: 0, sm: "0 16px 16px 0" },
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(to right, rgba(30, 30, 45, 0.9), rgba(25, 25, 35, 0.95))"
                : "linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(250, 252, 255, 0.97))",
            backdropFilter: "blur(10px)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: "1.5rem",
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, #fff, #aaa)"
                  : "linear-gradient(135deg, #333, #666)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow:
                theme.palette.mode === "dark"
                  ? "0 2px 4px rgba(0,0,0,0.3)"
                  : "none",
            }}
          >
            Upptäck lektioner
          </Typography>
          <IconButton onClick={onClose} sx={{}}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Search and Filter Bar */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Paper
            elevation={0}
            component="form"
            sx={{
              p: "2px 4px",
              display: "flex",
              alignItems: "center",
              width: "100%",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              background:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.background.paper, 0.4)
                  : alpha(theme.palette.background.paper, 0.7),
              backdropFilter: "blur(8px)",
              transition: "all 0.2s ease",
              "&:hover": {
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 2px 10px rgba(0,0,0,0.3)"
                    : "0 2px 10px rgba(0,0,0,0.1)",
              },
            }}
          >
            <IconButton sx={{ p: "10px" }} aria-label="search">
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Sök efter lektioner..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <IconButton
                sx={{ p: "10px" }}
                aria-label="clear search"
                onClick={() => setSearchQuery("")}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>

          <Tooltip title="Visa filter">
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              overlap="circular"
            >
              <IconButton
                onClick={toggleFilters}
                sx={{
                  bgcolor: showFilters
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <FilterListIcon />
              </IconButton>
            </Badge>
          </Tooltip>
        </Box>

        {/* Filter Section */}
        {/* Filter Section */}
        <Box
          sx={{
            overflow: "hidden",
            transition: "max-height 0.3s ease, opacity 0.3s ease",
            height: showFilters ? "440px" : "0px",
            opacity: showFilters ? 1 : 0,
            borderBottom: showFilters ? "1px solid" : "none",
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.paper, 0.4),
            backdropFilter: "blur(10px)",
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,

              visibility: showFilters ? "visible" : "hidden",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SubjectsList
                  selectedSubject={selectedSubject}
                  setSelectedSubject={setSelectedSubject}
                  selectedSubjects={selectedSubjects}
                  setSelectedSubjects={setSelectedSubjects}
                  setIsLoading={setIsLoading}
                  theme={theme}
                  compact={true}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <GradeLevels
                  selectedGrades={selectedGrades}
                  setSelectedGrades={setSelectedGrades}
                  setIsLoading={setIsLoading}
                  styles={styles}
                  theme={theme}
                  selectedSubject={selectedSubject}
                  selectedSubjects={selectedSubjects}
                  filteredItems={filteredLessons}
                  bankType="lektioner"
                  lessonTypeFilter={true}
                  selectedLessonTypes={selectedLessonTypes}
                  setSelectedLessonTypes={setSelectedLessonTypes}
                  compact={true}
                />
              </Grid>
              {activeFilterCount > 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 1,
                    }}
                  >
                    <Button
                      variant="text"
                      size="small"
                      onClick={clearFilters}
                      startIcon={<FilterAltOffIcon />}
                      sx={{
                        color: theme.palette.primary.main,
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        },
                      }}
                    >
                      Rensa alla filter
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>

        {/* Tabs Navigation */}
        <Box
          sx={{
            px: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              ".MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              ".MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9rem",
                minHeight: "48px",
              },
            }}
          >
            <Tab
              icon={<StarIcon sx={{ fontSize: "1.1rem" }} />}
              iconPosition="start"
              label="Populära"
            />
            <Tab
              icon={<HistoryIcon sx={{ fontSize: "1.1rem" }} />}
              iconPosition="start"
              label="Nya"
            />
            <Tab
              icon={<BookmarkIcon sx={{ fontSize: "1.1rem" }} />}
              iconPosition="start"
              label="Alla"
            />
          </Tabs>
        </Box>

        {/* Content Area */}
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            px: 3,
            py: 2,
          }}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : filteredLessons.length === 0 ? (
            <Box
              sx={{
                width: "100%",
                py: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <img
                src="/img/empty.png"
                alt="Tomt resultat"
                style={{
                  width: "180px",
                  marginBottom: "24px",
                  opacity: 0.8,
                  filter:
                    theme.palette.mode === "dark" ? "brightness(0.8)" : "none",
                }}
              />
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontWeight: "500",
                  fontSize: "1.1rem",
                  mb: 2,
                }}
              >
                Inga lektioner hittades
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  mb: 3,
                }}
              >
                Prova att ändra dina sökord eller filter
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={clearFilters}
                startIcon={<FilterAltOffIcon />}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 4px 12px rgba(0,0,0,0.4)"
                      : "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                Rensa filter
              </Button>
            </Box>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {tabValue === 2 ? (
                // If "Alla" tab is selected, group by lesson type
                sortedLessonTypes.map((lessonType) => (
                  <Box key={lessonType} sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        mb: 2,
                        pb: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {lessonType}
                      <Chip
                        label={groupedLessons[lessonType].length}
                        size="small"
                        sx={{
                          ml: 1.5,
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      />
                    </Typography>
                    <Grid container spacing={2}>
                      {groupedLessons[lessonType].map((lesson) => (
                        <Grid item xs={12} sm={12} key={lesson.id}>
                          <LessonCard
                            lesson={lesson}
                            onClick={handleLessonClick}
                            theme={theme}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))
              ) : (
                // For other tabs, show a flat list
                <Grid container spacing={2}>
                  {filteredLessons.map((lesson) => (
                    <Grid item xs={12} sm={12} key={lesson.id}>
                      <LessonCard
                        lesson={lesson}
                        onClick={handleLessonClick}
                        theme={theme}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </motion.div>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SwitchLessonsDrawer;
